// Лог сессии. Цель — понять, что на самом деле делал ребёнок:
// когда говорил, что услышали, какое число извлекли, какая задержка,
// куда пошёл мир, что двигал, какие правки создал и отменил.

export type LogKind =
  | "session_start"
  | "speech_start"
  | "speech_end"
  | "stt_start"
  | "stt_end"
  | "raw_transcript"
  | "number_extracted"
  | "no_number"
  | "queue"
  | "move_forward"
  | "move_back"
  | "jump"
  | "moment_enter"
  | "repeat_shake"
  | "over_horizon"
  | "freeze"
  | "drag_start"
  | "drag_drop"
  | "edit_created"
  | "edit_undone"
  | "character_event"
  | "tts_start"
  | "tts_end"
  | "stt_error"
  | "tts_error"
  | "speech_error"
  | "latency"
  | "phase";

export interface LogEntry {
  t: number; // ms от старта сессии
  kind: LogKind;
  data?: Record<string, unknown>;
}

export interface LatencySample {
  speechEnd: number;
  sttMs: number;
  numberAt: number;
  reactionAt: number;
  total: number; // speechEnd → reactionAt
}

export class SessionLog {
  readonly startedAt = Date.now();
  private entries: LogEntry[] = [];
  private latencies: LatencySample[] = [];
  private listeners = new Set<(e: LogEntry) => void>();

  add(kind: LogKind, data?: Record<string, unknown>) {
    const e: LogEntry = { t: Date.now() - this.startedAt, kind, data };
    this.entries.push(e);
    for (const fn of this.listeners) fn(e);
    return e;
  }

  addLatency(sample: LatencySample) {
    this.latencies.push(sample);
    this.add("latency", { ...sample });
  }

  onEntry(fn: (e: LogEntry) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  all(): LogEntry[] {
    return this.entries.slice();
  }

  /** Технические метрики (GDD 6.5 / п.31 брифа). */
  metrics() {
    const totals = this.latencies.map((l) => l.total).sort((a, b) => a - b);
    const sttMs = this.latencies.map((l) => l.sttMs).sort((a, b) => a - b);
    const pick = (arr: number[], q: number) =>
      arr.length ? arr[Math.min(arr.length - 1, Math.floor(q * arr.length))] : null;
    const heard = this.entries.filter((e) => e.kind === "raw_transcript").length;
    const numbers = this.entries.filter((e) => e.kind === "number_extracted").length;
    const misses = this.entries.filter((e) => e.kind === "no_number").length;
    const sttErrors = this.entries.filter((e) => e.kind === "stt_error").length;
    const ttsErrors = this.entries.filter((e) => e.kind === "tts_error").length;
    return {
      utterances: heard,
      numbersExtracted: numbers,
      missedNumbers: misses,
      recognitionRate: heard ? +(numbers / heard).toFixed(2) : null,
      medianLatencyMs: pick(totals, 0.5),
      p95LatencyMs: pick(totals, 0.95),
      medianSttMs: pick(sttMs, 0.5),
      sttErrors,
      ttsErrors,
      samples: this.latencies.length,
    };
  }

  export() {
    return {
      startedAt: new Date(this.startedAt).toISOString(),
      durationMs: Date.now() - this.startedAt,
      metrics: this.metrics(),
      latencies: this.latencies,
      entries: this.entries,
    };
  }
}
